/**
 * @alias display.StateLayoutController
 * @classdesc Manages screen layout states
 *
 * @param scene Scene which hosts layout controller
 * @param { StateLayoutControllerConfig } config for layout management
 *
 */
class StateLayoutController extends Phaser.Events.EventEmitter {
  constructor(scene, config) {
    super();

    this._scene = scene;
    this._states = config?.states;
    this._contentScope = config.contentScope || scene;
    this._currentState = null;
    this._stateChangeDuration = config?.duration || 250;
  }

  /**
   * Get the current state
   */
  get state() {
    return this._currentState;
  }

  /**
   * Set the layout state, will trigger a transition from current layout to requested
   * @param {string} value id of layout state to transition to
   * @param {bool} [instant=false] if true, will skip transition and jump to state
   */
  setState(value, instant) {
    if (this._currentState === value) return;

    // set current state and grab config
    this._currentState = value;
    const config = this._states[this._currentState];
    if (config) {
      // if not instant state change, create a timeline
      this._timeline = instant ? false : this._createTimeline();

      // for each entry in the state config
      config.forEach((entry) => {
        // find the target on the content scope
        const targets = this._contentScope[entry.id];
        if (!targets) return;

        if (this._timeline) {
          // if we are using a timeline, create and add a tween to timeline
          const tween = { targets, offset: 0, duration: this._stateChangeDuration };
          Object.keys(entry).forEach((key) => {
            if (key !== 'id') tween[key] = entry[key];
          });
          this._timeline.add(tween);
        } else {
          // if no timeline, just update targets properties directly
          Object.keys(entry).forEach((key) => {
            if (key !== 'id') targets[key] = entry[key];
          });
        }
      });

      // either trigger the timeline or display complete as needed
      if (this._timeline) this._timeline.play();
      else this._dispatchStateChangeComplete();
    }
  }

  destroy() {
    this._clearTimeline();
    this._scene = null;
    this._states = null;
    this._contentScope = null;
    this._currentState = null;
    this._stateChangeDuration = null;
  }

  // internal

  _createTimeline() {
    this._clearTimeline();
    this._timeline = this._scene.tweens.createTimeline({
      onComplete: this._dispatchStateChangeComplete,
      onCompleteScope: this,
    });
    return this._timeline;
  }

  _clearTimeline() {
    if (this._timeline) {
      this._timeline.destroy();
      this._timeline = null;
    }
  }

  _dispatchStateChangeComplete() {
    this.emit('COMPLETE', this._currentState);
  }
}

/**
 * @typedef StateLayoutControllerConfig
 * @property {Object<string,object>} states key/value config for each state and display properties
 * @property {object} [contentScope=scene] content scope for reference objects, if not propvided scene will be used
 * @property {duration} [duration=250] default duration for state transitions
 */

export { StateLayoutController };
